/**
 * Тепловая карта ошибок и задержек (UX-06, ТЗ UI-33).
 *
 * Пять уровней, а не четыре: нулевой — «почти не промахивается» — нужен, чтобы
 * освоенное не выглядело слабым местом просто потому, что попало в список.
 *
 * Цвет здесь ничего не решает в одиночку: в каждой ячейке стоит число, а под
 * картой — легенда диапазонов. В оттенках серого уровень читается по числу и
 * по легенде, а не по заливке (UX-13).
 *
 * Цвета берутся смысловыми именами `heat-0 … heat-4`: пары «фон и текст к
 * нему» рассчитаны и проверены тестом контраста в обеих темах.
 */

/** Границы уровней по интенсивности: пять диапазонов по 20 %. */
const STEP = 0.2;
export const HEAT_LEVELS = [0, 1, 2, 3, 4] as const;
export type HeatLevel = (typeof HEAT_LEVELS)[number];

export interface HeatCell {
  readonly label: string;
  /** Интенсивность проблемы, 0..1. */
  readonly intensity: number;
}

export interface HeatMapProps {
  readonly cells: readonly HeatCell[];
  readonly caption: string;
  /**
   * Подпись легенды, например «Доля промахов».
   *
   * Без неё легенда не показывается: пять безымянных полосок объясняют
   * меньше, чем ничего.
   */
  readonly legendLabel?: string | undefined;
}

/** Уровень ячейки: 0 — почти нет промахов, 4 — чаще всего. */
export function heatLevel(intensity: number): HeatLevel {
  const level = Math.floor(Math.min(0.999, Math.max(0, intensity)) / STEP);
  return HEAT_LEVELS[level] ?? 4;
}

/** Границы уровня в процентах: их и показывает легенда. */
function range(level: HeatLevel): string {
  const from = Math.round(level * STEP * 100);
  const to = Math.round((level + 1) * STEP * 100);
  return `${from}–${to}%`;
}

export function HeatMap({ cells, caption, legendLabel }: HeatMapProps): React.JSX.Element {
  return (
    <figure className="t2x-heatmap">
      <figcaption className="t2x-heatmap__caption">{caption}</figcaption>

      <ul className="t2x-heatmap__grid">
        {cells.map((cell) => (
          <li
            key={cell.label}
            className={`t2x-heatmap__cell t2x-heatmap__cell--${heatLevel(cell.intensity)}`}
          >
            <span className="t2x-heatmap__label">{cell.label}</span>
            <span className="t2x-heatmap__value">{Math.round(cell.intensity * 100)}%</span>
          </li>
        ))}
      </ul>

      {legendLabel !== undefined && (
        <ul className="t2x-heatmap__legend" aria-label={legendLabel}>
          {HEAT_LEVELS.map((level) => (
            <li key={level} className="t2x-heatmap__legend-item">
              <span
                className={`t2x-heatmap__swatch t2x-heatmap__cell--${level}`}
                aria-hidden="true"
              />
              {range(level)}
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}
